import React, { useEffect, useMemo, useState } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import { MapPin, Users, Search } from 'lucide-react';
import { stadiumsAPI } from '../services/api';
import SEOHead from '../components/SEOHead';

const STADIUM_HERO_IMG =
  'https://lh3.googleusercontent.com/aida-public/AB6AXuAerOJXXlkZXoU9kIlFgaaRTLR2T8A_sR50EXwaS6rNOZBlWaEua-4b086sMDM33KCNldHixutZjwcXh0dBy8P7-JyxiXY3mu79I8yU3glD-deduy_s2JT8yrVtKd4wg7O7ajidqfDxR2AiMfgY4vBzj56SRNB4Hoj42V5X3ZjccSF3KiEOYNn2_fXkleE8Bel4p7FulP-61JzPe4nFJM9xeRL7hLrq0UiQNcS3wVPF-s9yCFCRZerg94EZNo9WBpNhvUD4VNL2LINe';

function nationBadge(country) {
  const c = (country || '').toLowerCase();
  if (c.includes('mexico')) return { label: 'Mexico', className: 'bg-tertiary text-on-tertiary' };
  if (c.includes('canada')) return { label: 'Canada', className: 'bg-secondary text-on-secondary' };
  return { label: 'USA', className: 'bg-tertiary text-on-tertiary' };
}

function isUsaCountry(country) {
  const c = (country || '').toLowerCase();
  return c.includes('united states') || c === 'usa' || c.includes('u.s.');
}

function StadiumCardPhoto({ src, alt }) {
  const [failed, setFailed] = useState(false);
  useEffect(() => {
    setFailed(false);
  }, [src]);
  if (!src || failed) return null;
  return (
    <img
      src={src}
      alt={alt}
      loading="lazy"
      decoding="async"
      className="absolute inset-0 z-0 w-full h-full object-cover"
      onError={() => setFailed(true)}
    />
  );
}

const Stadiums = () => {
  const [searchParams, setSearchParams] = useSearchParams();
  const qParam = (searchParams.get('q') || '').trim();
  const [nation, setNation] = useState('all');
  const [localQ, setLocalQ] = useState(qParam);

  useEffect(() => {
    setLocalQ(qParam);
  }, [qParam]);

  const { data: stadiums = [], isLoading } = useQuery({ queryKey: ['stadiums'], queryFn: () => stadiumsAPI.getAll().then((r) => r.data) });

  const filtered = useMemo(() => {
    let list = stadiums;
    if (nation === 'usa') list = list.filter((s) => isUsaCountry(s.country));
    else if (nation === 'mexico') list = list.filter((s) => (s.country || '').toLowerCase().includes('mexico'));
    else if (nation === 'canada') list = list.filter((s) => (s.country || '').toLowerCase().includes('canada'));
    const q = qParam.toLowerCase();
    if (q) {
      list = list.filter((s) => {
        const hay = `${s.name} ${s.city} ${s.country}`.toLowerCase();
        return hay.includes(q);
      });
    }
    return list;
  }, [stadiums, nation, qParam]);

  const applySearch = (e) => {
    e.preventDefault();
    const next = localQ.trim();
    if (next) setSearchParams({ q: next });
    else setSearchParams({});
  };

  if (isLoading) {
    return (
      <>
        <SEOHead
          title="2026 World Cup Stadiums - WorldCup Fan Planner"
          description="Host venues for the 2026 FIFA World Cup across the USA, Canada, and Mexico—capacity, cities, and stadium guides."
        />
        <div className="min-h-[40vh] flex items-center justify-center text-on-surface-variant dark:text-slate-400">Loading stadiums…</div>
      </>
    );
  }

  const chip = (id, label) => {
    const active = nation === id;
    return (
      <button
        type="button"
        onClick={() => setNation(id)}
        className={`px-5 py-2 rounded-full font-bold text-sm transition-all ${
          active
            ? 'bg-primary text-on-primary shadow-lg shadow-primary/20'
            : 'bg-surface-container-high dark:bg-slate-800 text-on-surface dark:text-slate-200 hover:bg-secondary-container/40'
        }`}
      >
        {label}
      </button>
    );
  };

  return (
    <>
      <SEOHead
        title="2026 World Cup Stadiums - WorldCup Fan Planner"
        description="Host venues for the 2026 FIFA World Cup across the USA, Canada, and Mexico—capacity, cities, and stadium guides."
      />
      <main className="max-w-screen-2xl mx-auto px-4 sm:px-6 lg:px-8 py-10 sm:py-12 font-body">
        <header className="relative mb-12 sm:mb-16 overflow-hidden rounded-4xl bg-primary h-[320px] sm:h-[400px] flex items-end p-6 sm:p-10 md:p-16">
          <div className="absolute inset-0 z-0">
            <img src={STADIUM_HERO_IMG} alt="" className="w-full h-full object-cover opacity-40 mix-blend-overlay" />
            <div className="absolute inset-0 bg-gradient-to-t from-primary via-primary/40 to-transparent" aria-hidden />
          </div>
          <div className="relative z-10 max-w-3xl">
            <h1 className="font-headline text-4xl sm:text-6xl md:text-7xl font-bold text-on-primary leading-tight tracking-tighter mb-4">
              THE KINETIC STAGES
            </h1>
            <p className="text-base sm:text-lg md:text-xl text-primary-container font-medium max-w-xl">
              16 cities. 3 nations. One global celebration. Explore the venues hosting the beautiful game in 2026.
            </p>
          </div>
        </header>

        <section className="mb-10 flex flex-col lg:flex-row gap-6 items-stretch lg:items-center justify-between" aria-label="Filter stadiums">
          <div className="flex flex-wrap gap-3">
            {chip('all', 'All Nations')}
            {chip('usa', 'USA')}
            {chip('mexico', 'Mexico')}
            {chip('canada', 'Canada')}
          </div>
          <form onSubmit={applySearch} className="w-full lg:w-96 relative">
            <input
              value={localQ}
              onChange={(e) => setLocalQ(e.target.value)}
              className="w-full bg-surface-container-low dark:bg-slate-800 border-none rounded-xl py-3.5 pl-12 pr-4 focus:ring-2 focus:ring-primary focus:bg-surface-container-lowest dark:focus:bg-slate-900 transition-all text-on-surface dark:text-slate-100 placeholder:text-on-surface-variant"
              placeholder="Search for a city or stadium..."
              type="search"
              aria-label="Search stadiums"
            />
            <Search className="absolute left-4 top-1/2 -translate-y-1/2 h-5 w-5 text-outline dark:text-slate-500 pointer-events-none" aria-hidden />
          </form>
        </section>

        <p className="text-sm text-on-surface-variant dark:text-slate-400 mb-8">
          Tie venues to{' '}
          <Link to="/matches" className="text-primary font-semibold hover:underline">
            the match schedule
          </Link>{' '}
          and your{' '}
          <Link to="/travel-planner" className="text-primary font-semibold hover:underline">
            travel plan
          </Link>
          .
        </p>

        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
          {filtered.map((s) => {
            const badge = nationBadge(s.country);
            return (
              <article
                key={s.id}
                className="group relative bg-surface-container-lowest dark:bg-slate-800/70 rounded-4xl overflow-hidden flex flex-col transition-all hover:-translate-y-2 border border-outline-variant/10 dark:border-slate-700 shadow-sm hover:shadow-fanhub"
              >
                <div className="h-56 relative overflow-hidden bg-gradient-to-br from-surface-container-high to-surface-container dark:from-slate-700 dark:to-slate-900">
                  <StadiumCardPhoto src={s.image} alt={`${s.name}, ${s.city}`} />
                  <div className={`absolute top-4 left-4 z-20 px-3 py-1 rounded-full text-xs font-bold uppercase tracking-widest ${badge.className}`}>
                    {badge.label}
                  </div>
                  <div className="absolute inset-0 z-10 bg-gradient-to-t from-black/50 to-transparent opacity-70 pointer-events-none" aria-hidden />
                </div>
                <div className="p-6 flex flex-col flex-grow">
                  <h2 className="font-headline text-xl font-bold tracking-tight text-on-surface dark:text-white mb-3">
                    {s.city}
                  </h2>
                  <div className="space-y-2 mb-4 text-on-surface-variant dark:text-slate-400 text-sm">
                    <div className="flex items-center gap-2">
                      <MapPin className="h-4 w-4 shrink-0 text-secondary" aria-hidden />
                      <span className="font-semibold text-on-surface dark:text-slate-200">{s.name}</span>
                    </div>
                    <div className="flex items-center gap-2">
                      <Users className="h-4 w-4 shrink-0" aria-hidden />
                      <span>{s.capacity != null ? `${Number(s.capacity).toLocaleString()} capacity` : 'Capacity TBD'}</span>
                    </div>
                  </div>
                  <p className="text-sm text-on-surface-variant dark:text-slate-400 line-clamp-2 mb-6 flex-grow">
                    Host venue in {s.city}, {s.country}. Open the guide for maps, FAQs, and city context.
                  </p>
                  <div className="mt-auto flex items-center justify-between gap-3 flex-wrap">
                    <Link
                      to="/hotels"
                      className="bg-secondary text-on-secondary px-4 py-2 rounded-xl font-bold text-sm hover:bg-secondary-dim transition-colors"
                    >
                      Book Travel
                    </Link>
                    <Link to={`/stadiums/${s.slug || s.id}`} className="text-primary font-bold text-sm hover:underline">
                      Details
                    </Link>
                  </div>
                </div>
              </article>
            );
          })}
        </div>

        {filtered.length === 0 && (
          <p className="text-center text-on-surface-variant dark:text-slate-400 py-12">No stadiums match your filters.</p>
        )}

        <section className="mt-20 grid lg:grid-cols-2 gap-10 items-center bg-surface-container-low dark:bg-slate-900/40 rounded-4xl p-8 sm:p-10 border border-outline-variant/10 dark:border-slate-800">
          <div className="h-72 rounded-3xl bg-gradient-to-br from-primary/20 via-secondary/20 to-tertiary/20 border border-outline-variant/10" aria-hidden />
          <div>
            <p className="text-primary-container font-bold text-xs uppercase tracking-widest mb-2">Destination highlight</p>
            <h2 className="font-headline text-2xl sm:text-3xl font-bold text-primary dark:text-primary-container mb-4">STADIUM SOUL: THE FAN ZONES</h2>
            <p className="text-on-surface-variant dark:text-slate-400 leading-relaxed mb-6">
              Every host city layers official fan festivals, public screens, and local culture around the venues. Use our city guides and itinerary planner to line up
              fan zones with your match tickets.
            </p>
            <div className="grid sm:grid-cols-2 gap-4 mb-6">
              <div className="bg-surface-container-lowest dark:bg-slate-800/80 rounded-2xl p-4 border border-outline-variant/10">
                <h3 className="font-bold text-on-surface dark:text-white text-sm mb-1">Local flavors</h3>
                <p className="text-xs text-on-surface-variant dark:text-slate-500">Food halls, markets, and match-day pop-ups.</p>
              </div>
              <div className="bg-surface-container-lowest dark:bg-slate-800/80 rounded-2xl p-4 border border-outline-variant/10">
                <h3 className="font-bold text-on-surface dark:text-white text-sm mb-1">Fan festivals</h3>
                <p className="text-xs text-on-surface-variant dark:text-slate-500">Big screens and concerts between kickoffs.</p>
              </div>
            </div>
            <Link
              to="/cities"
              className="inline-flex items-center px-6 py-3 rounded-full bg-primary text-on-primary font-bold text-sm hover:brightness-110 transition-all"
            >
              Explore host cities
            </Link>
          </div>
        </section>
      </main>
    </>
  );
};

export default Stadiums;
